import { CheckCircleIcon, PlugsConnectedIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  BODY_TYPES,
  FUEL_TYPES,
  PRIORITY_CODES,
  USAGE_SCENARIOS,
  bodyTypeKey,
  fuelKey,
  priorityKey,
  usageKey,
  usePatchCriteria,
  type CriteriaState,
} from '@entities/search-session';
import { Button } from '@shared/ui';

import {
  MAX_AGE_NO_LIMIT,
  fallbackDefaults,
  fallbackOperations,
  maxAgeOptions,
  parseAmount,
  type FallbackFormValues,
} from '../model/fallbackForm';

import styles from './Criteria.module.css';

interface CriteriaFallbackFormProps {
  sessionId: string;
  criteria: CriteriaState;
}

const DRIVETRAIN_OPTIONS = ['any', 'awd_required', 'awd_preferred', 'fwd', 'rwd'] as const;
const TRANSMISSION_OPTIONS = ['any', 'automatic', 'manual'] as const;

/**
 * Резервная форма при недоступности консультанта (FE-072, MVP_PRD §24).
 *
 * Отказ LLM не должен обрывать подбор: сообщение пользователя сохранено, а
 * критерии можно задать напрямую. Дальше всё идёт обычным путём — ранжирование
 * не зависит ни от доступности модели, ни от того, откуда пришли критерии.
 *
 * Полей ровно столько, сколько нужно для готовности к поиску (MVP_PRD §11):
 * бюджет с режимом, кузов, возраст, сценарий, топливо и приоритет —
 * обязательны, годовой пробег, привод и коробка — по желанию. Акцентов и
 * допуска бюджета в форме нет: они существуют только вместе с цитатой.
 */
export function CriteriaFallbackForm({ sessionId, criteria }: CriteriaFallbackFormProps) {
  const { t } = useTranslation();
  const formId = useId();
  const patch = usePatchCriteria(sessionId);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FallbackFormValues>({ defaultValues: fallbackDefaults(criteria.payload) });

  const budgetMode = watch('budgetMode');
  const ageOptions = maxAgeOptions(fallbackDefaults(criteria.payload).maxAge);

  const submit = handleSubmit((values) => {
    patch.mutate({
      criteriaVersionId: criteria.version_id,
      operations: fallbackOperations(values),
    });
  });

  if (patch.isSuccess) {
    return (
      <div className={styles.fallbackDone} role="status">
        <CheckCircleIcon size={20} weight="fill" aria-hidden="true" />
        <p>{t('criteria.fallback.done')}</p>
      </div>
    );
  }

  return (
    <form
      className={styles.fallback}
      noValidate
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <header className={styles.fallbackHead}>
        <span className={styles.fallbackIcon} aria-hidden="true">
          <PlugsConnectedIcon size={18} weight="bold" />
        </span>
        <div>
          <h3 className={styles.fallbackTitle}>{t('criteria.fallback.title')}</h3>
          <p className={styles.fallbackLead}>{t('criteria.fallback.lead')}</p>
        </div>
      </header>

      <div className={styles.fallbackGrid}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-budget`}>
            {t('criteria.fallback.budget')}
          </label>
          <input
            id={`${formId}-budget`}
            className={styles.input}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-invalid={errors.budgetEuros ? true : undefined}
            {...register('budgetEuros', {
              validate: (value) => {
                const amount = parseAmount(value);
                return amount !== null && amount > 0;
              },
            })}
          />
          {errors.budgetEuros ? (
            <p className={styles.fieldError} role="alert">
              {t('criteria.fallback.budgetRequired')}
            </p>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-budget-mode`}>
            {t('criteria.field.budgetMode')}
          </label>
          <select id={`${formId}-budget-mode`} className={styles.input} {...register('budgetMode')}>
            <option value="vehicle_only">{t('criteria.budgetMode.vehicle_only')}</option>
            <option value="total">{t('criteria.budgetMode.total')}</option>
          </select>
        </div>

        {budgetMode === 'total' ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${formId}-reserve`}>
              {t('criteria.fallback.reserve')}
            </label>
            <input
              id={`${formId}-reserve`}
              className={styles.input}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              {...register('reserveEuros')}
            />
          </div>
        ) : null}

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-body`}>
            {t('criteria.field.bodyTypes')}
          </label>
          <select id={`${formId}-body`} className={styles.input} {...register('bodyType')}>
            {BODY_TYPES.map((code) => (
              <option key={code} value={code}>
                {t(bodyTypeKey(code))}
              </option>
            ))}
            <option value="any">{t('criteria.value.anyBody')}</option>
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-age`}>
            {t('criteria.field.maxAge')}
          </label>
          <select id={`${formId}-age`} className={styles.input} {...register('maxAge')}>
            {ageOptions.map((option) => (
              <option key={option} value={option}>
                {option === MAX_AGE_NO_LIMIT
                  ? t('criteria.value.noLimit')
                  : t('criteria.value.years', { count: Number(option) })}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-mileage`}>
            {t('criteria.fallback.annualMileage')}
          </label>
          <input
            id={`${formId}-mileage`}
            className={styles.input}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            {...register('annualMileageKm')}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-usage`}>
            {t('criteria.field.usage')}
          </label>
          <select id={`${formId}-usage`} className={styles.input} {...register('usage')}>
            {USAGE_SCENARIOS.map((code) => (
              <option key={code} value={code}>
                {t(usageKey(code))}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-drivetrain`}>
            {t('criteria.field.drivetrain')}
          </label>
          <select id={`${formId}-drivetrain`} className={styles.input} {...register('drivetrain')}>
            {DRIVETRAIN_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`criteria.fallback.drivetrain.${option}`)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-transmission`}>
            {t('criteria.field.transmission')}
          </label>
          <select
            id={`${formId}-transmission`}
            className={styles.input}
            {...register('transmission')}
          >
            {TRANSMISSION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option === 'any'
                  ? t('criteria.value.anyOption')
                  : t(`catalog.transmission.${option}`)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-priority`}>
            {t('criteria.fallback.priority')}
          </label>
          <select id={`${formId}-priority`} className={styles.input} {...register('priority')}>
            {PRIORITY_CODES.map((code) => (
              <option key={code} value={code}>
                {t(priorityKey(code))}
              </option>
            ))}
          </select>
        </div>
      </div>

      <fieldset className={styles.fuelSet}>
        <legend className={styles.label}>{t('criteria.fallback.fuels')}</legend>
        <div className={styles.fuelChips}>
          {FUEL_TYPES.map((code) => (
            <label key={code} className={styles.fuelChip}>
              <input
                type="checkbox"
                value={code}
                {...register('fuels', { validate: (value) => value.length > 0 })}
              />
              <span>{t(fuelKey(code))}</span>
            </label>
          ))}
        </div>
        {errors.fuels ? (
          <p className={styles.fieldError} role="alert">
            {t('criteria.fallback.fuelsRequired')}
          </p>
        ) : null}
      </fieldset>

      {patch.isError ? (
        <p className={styles.error} role="alert">
          {t('criteria.errors.patchFailed')}
        </p>
      ) : null}

      <Button type="submit" disabled={patch.isPending} fullWidth>
        {patch.isPending ? t('criteria.fallback.saving') : t('criteria.fallback.submit')}
      </Button>
    </form>
  );
}
